<template>
  <div
    class="i-questionnaire-coaching"
    :class="{ report: isReport }"
  >
    <QuizHeader
      :current-q="currentQNumber || lastQNum"
      :total-q="TOTAL_QUESTIONS"
      :hide-count="isReport"
    />
    <QuizProgress :percent="progressPercent" />

    <div class="body">
      <!-- Question screens -->
      <template
        v-for="screen in questionScreens"
        :key="screen.id"
      >
        <QuizScreen :active="currentScreenId === screen.id">
          <img
            v-if="currentScreenId > 2"
            class="previous"
            :src="Arrow"
            @click="previous"
          />
          <QuizQuestion
            v-if="screen.type === 'question'"
            :question-number="screen.questionNumber"
            :title="screen.title"
            :options="screen.options"
            :callout-triggers="screen.calloutTriggers || []"
            :callout-texts="screen.id === 5 ? callout5Texts : {}"
            :default-callout-html="screen.defaultCalloutHtml || ''"
            :situation="answers.situation || ''"
            @answer="onAnswer(screen, $event)"
            @continue="advance"
          />
          <QuizInsight
            v-else-if="screen.type === 'insight'"
            :icon="screen.icon"
            :title="insightTitle(screen)"
            :body="insightBody(screen)"
            @continue="advance"
          />
        </QuizScreen>
      </template>

      <!-- Email gate -->
      <QuizScreen :active="currentScreenId === 15">
        <EmailGate @submit="onEmailSubmit" />
      </QuizScreen>

      <!-- Report -->
      <QuizScreen :active="currentScreenId === 16">
        <QuizReport :answers="answers" />
      </QuizScreen>
    </div>

    <QuizStickyBar
      :visible="showStickyBar"
      :label="buttonDetails.stickyLabel"
      :btn-text="buttonDetails.stickyCta"
      @checkout="buttonDetails.ctaAction"
    />

    <QuizInterstitial :visible="interstitialVisible" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue"
import { useRoute } from "vue-router"

import api from "@/services/apiClient"
import GtmService, { UserFlow } from "@/services/gtmService"

import QuizHeader from "@/components/questionnaireCoaching/QuizHeader.vue"
import QuizProgress from "@/components/questionnaireCoaching/QuizProgress.vue"
import QuizScreen from "@/components/questionnaireCoaching/QuizScreen.vue"
import QuizQuestion from "@/components/questionnaireCoaching/QuizQuestion.vue"
import QuizInsight from "@/components/questionnaireCoaching/QuizInsight.vue"
import EmailGate from "@/components/questionnaireCoaching/EmailGate.vue"
import QuizReport from "@/components/questionnaireCoaching/QuizReport.vue"
import QuizStickyBar from "@/components/questionnaireCoaching/QuizStickyBar.vue"
import QuizInterstitial from "@/components/questionnaireCoaching/QuizInterstitial.vue"
import {
  QUIZ_SCREENS,
  TOTAL_QUESTIONS,
  QUESTION_SCREENS,
  INSIGHT_TEXTS,
  CALLOUT_5_TEXTS,
  useQuestionnaireState,
  getPathContent,
  adaptContentForLevel
} from "@/composables/useQuestionnaire.js"

// ASSETS
import Arrow from "@/assets/icons/icon-arrow-circle-left.svg"

const route = useRoute()
const { answers, currentScreenId, currentQNumber, progressPercent, setAnswer, advance, goToScreen, previous } =
  useQuestionnaireState()

const interstitialVisible = ref(false)
const lastQNum = ref(1)
const showStickyBar = ref(false)

const questionScreens = QUIZ_SCREENS.filter((s) => s.type !== "emailGate" && s.type !== "report")
const callout5Texts = CALLOUT_5_TEXTS

const isReport = computed(() => currentScreenId.value === 16)

const buttonDetails = computed(() => {
  const content = adaptContentForLevel(
    getPathContent(answers.value.situation, answers.value.email),
    answers.value.roleLevel
  )
  return { stickyLabel: content.stickyLabel, stickyCta: content.stickyCta, ctaAction: content.ctaAction }
})

function insightTitle(screen) {
  // Screen 6 title is personalised based on Q1 situation answer
  if (screen.id === 6) {
    const txt = INSIGHT_TEXTS[answers.value.situation]
    return txt ? txt.title : screen.title
  }
  return screen.title
}

function insightBody(screen) {
  // Screen 6 body is personalised based on Q1 situation answer
  if (screen.id === 6) {
    const txt = INSIGHT_TEXTS[answers.value.situation]
    return txt ? txt.body : screen.body
  }
  return screen.body
}

function onAnswer(screen, value) {
  setAnswer(screen.answerKey, value)
  // Screens with callouts show a Continue button — advance is triggered by @continue
  // Screens without callouts auto-advance after a short delay
  if (!screen.calloutTriggers?.length || !screen.calloutTriggers.includes(value)) {
    setTimeout(() => advance(), 400)
  }
}

async function onEmailSubmit(email) {
  setAnswer("email", email)
  interstitialVisible.value = true
  GtmService.newQuizCompleteEvent(UserFlow.COACHING)

  try {
    await api.user.processCoachingFeasibilityData(answers.value)
  } catch (error) {
    console.error("Error sending this user's answers and email to be processed via make.com", error)
  }

  interstitialVisible.value = false
  goToScreen(16)

  GtmService.newAssessmentViewEvent(UserFlow.COACHING)
}

function handleScroll() {
  if (currentScreenId.value === 16 && window.scrollY > 400) {
    showStickyBar.value = true
  } else {
    showStickyBar.value = false
  }
}

watch(currentQNumber, (v) => {
  if (v !== null) lastQNum.value = v
})

onMounted(() => {
  // Pre-fill situation from query param (from landing page)
  const situation = route.query.situation
  if (situation) {
    setAnswer("situation", situation)
    // skip screen 1 and go to screen 2
    goToScreen(2)
  }
  window.addEventListener("scroll", handleScroll)
})

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll)
})
</script>

<style lang="scss" scoped>
.i-questionnaire-coaching {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
  background: $white;

  &.report {
    background: #f9fafb;
  }

  .previous {
    position: absolute;
    top: 0;
    left: 0;
    cursor: pointer;
  }

  .body {
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 80px 0 60px;
    position: relative;
    min-height: calc(100dvh - 51px); // account for fixed header + progress bar

    @include respond-to($breakpoint-md) {
      padding: 96px 0 60px;
      align-items: flex-start;
    }

    // Active (non-transitioning) screen sits in normal flow, centred
    > :deep(.i-quiz-screen) {
      padding: 0 16px;

      @include respond-to($breakpoint-sm) {
        padding: 0 20px;
      }

      @include respond-to($breakpoint-md) {
        padding: 0 40px;
      }
    }
  }
}
</style>

<style lang="scss">
.i-questionnaire-coaching {
  font-family: $sans;
  background: $white;
  color: $ink;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;

  &.report {
    .proof-case,
    .coach-card,
    .case-study {
      background: $white;
    }
  }

  ::selection {
    background: $navy-pale;
    color: $amber-mid;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
